import Vue from 'vue';
import { BusinessRegistrationService } from '../_services/BusinessRegistration.Service';
import {
  hasBrListSubmissionActivity,
  hasBrVerifiedDocument,
} from './brDocumentCore';
import { isBrFeatureEnabled, isBrSubmissionDeadlinePassed } from './brConfig';
import { normalizeBrApiItem, unwrapBrApiRows } from './brList';
import { BR_STATUS } from './brStatus';

export const brRuntimeState = Vue.observable({
  hasSubmitted: null,
  summary: null,
  documentList: [],
  nameChangePending: [],
  dataLoadedAt: 0,
  /**
   * 最近一次列表加载错误：null | 'SUMMARY' | 'LIST' | 'NAME_CHANGE' | 'PARTIAL'
   * 供页面 toast；失败勿当「无证照」。
   */
  lastLoadError: null,
  /** AccessGate 快照（响应式，供侧栏锁定刷新） */
  kybGate: null,
  /** AccessGate 请求中（无可用快照时用于侧栏「确认中」态） */
  kybGateLoading: false,
});

let brPostLoginPromptPending = false;
let statusLoadPromise = null;

const ACCESS_GATE_TTL_MS = 45 * 1000;
let accessGateCache = {
  fetchedAt: 0,
  empCompanyId: null,
  gate: null,
  inflight: null,
};

/** 清空 gate 后仍保留，避免侧栏在短暂无 gate 时无法用 lock meta 兜底 */
let kybLockMeta = {
  featureEnabled: null,
  lockActive: null,
  lockStartDate: null,
};

function normalizeAccessGate(raw) {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  return {
    featureEnabled: !!(raw.featureEnabled ?? raw.FeatureEnabled),
    lockActive: !!(raw.lockActive ?? raw.LockActive),
    allowed: !!(raw.allowed ?? raw.Allowed),
    reason: String(raw.reason || raw.Reason || ''),
    landingPath: String(raw.landingPath || raw.LandingPath || '/businessDocuments/default'),
    effectiveBrCount: Number(raw.effectiveBrCount ?? raw.EffectiveBrCount ?? 0) || 0,
    lockStartDate: raw.lockStartDate || raw.LockStartDate || null,
  };
}

function rememberKybLockMeta(gate) {
  if (!gate) {
    return;
  }
  kybLockMeta = {
    featureEnabled: gate.featureEnabled,
    lockActive: gate.lockActive,
    lockStartDate: gate.lockStartDate || null,
  };
}

function writeAccessGateCache({ gate = null, empCompanyId = null, fetchedAt = 0 } = {}) {
  accessGateCache = {
    gate,
    empCompanyId,
    fetchedAt,
    inflight: accessGateCache.inflight,
  };
  if (gate) {
    rememberKybLockMeta(gate);
  }
  brRuntimeState.kybGate = gate;
}

/**
 * 使 AccessGate 缓存过期。
 * @param {{ clearGate?: boolean }} [options] clearGate=true 时清空快照（登出/换号）；默认保留旧 gate（SWR，避免侧栏闪锁）
 */
export function invalidateKybAccessGateCache({ clearGate = false } = {}) {
  accessGateCache.inflight = null;
  accessGateCache.fetchedAt = 0;
  if (clearGate) {
    kybLockMeta = {
      featureEnabled: null,
      lockActive: null,
      lockStartDate: null,
    };
    brRuntimeState.kybGateLoading = false;
    writeAccessGateCache({ gate: null, empCompanyId: null, fetchedAt: 0 });
  }
}

export function seedKybAccessGateFromLogin(loginData) {
  const gate = normalizeAccessGate(loginData?.kybAccessGate ?? loginData?.KybAccessGate);
  if (!gate) {
    return false;
  }
  brRuntimeState.kybGateLoading = false;
  writeAccessGateCache({
    gate,
    empCompanyId: loginData.empCompanyId ?? loginData.EmpCompanyId ?? null,
    fetchedAt: Date.now(),
  });
  return true;
}

export function getEffectiveKybLockStartDate() {
  const raw = accessGateCache.gate?.lockStartDate ?? kybLockMeta.lockStartDate;
  return raw ? String(raw).trim() : '';
}

export function isKybLockPeriodActiveByCache() {
  const gate = accessGateCache.gate;
  if (gate) {
    if (!gate.featureEnabled) {
      return false;
    }
    return !!gate.lockActive;
  }
  if (kybLockMeta.featureEnabled === false) {
    return false;
  }
  if (kybLockMeta.lockActive != null) {
    return !!kybLockMeta.lockActive;
  }
  // gate 丢失时：有过 LockStartDate 则按日期推算是否已进锁定期
  const lockStart = getEffectiveKybLockStartDate();
  if (lockStart && kybLockMeta.featureEnabled !== false) {
    return isBrSubmissionDeadlinePassed(new Date(), lockStart);
  }
  return null;
}

/**
 * 是否应锁侧栏。
 * - 有 AccessGate：权威（feature + lockActive + allowed）
 * - gate 被清空/失败时：若已知已进锁定期，用本地「有无已生效未过期 BR」兜底
 * - 仍无法判断时返回 null（由 shouldLockSidebarForBr 按「未知则从严」处理）
 */
export function isKybLockedByCache() {
  const gate = accessGateCache.gate;
  if (gate) {
    if (!gate.featureEnabled || !gate.lockActive) {
      return false;
    }
    return !gate.allowed;
  }
  const lockPeriod = isKybLockPeriodActiveByCache();
  if (lockPeriod === false) {
    return false;
  }
  if (lockPeriod === true && brRuntimeState.dataLoadedAt > 0) {
    // 核心逻辑：锁定期内无「已生效且未过期」→ 必须锁（仅驳回/待审/待生效都不解锁）
    return !isBrUnlocked();
  }
  return null;
}

/** 冷启动：正在拉 AccessGate 且尚无可用锁定结论 */
export function isKybAccessGatePending() {
  return !!brRuntimeState.kybGateLoading && isKybLockedByCache() === null;
}

export async function fetchKybAccessGate(user, { force = false } = {}) {
  if (!isBrFeatureEnabled()) {
    brRuntimeState.kybGateLoading = false;
    return null;
  }
  const empCompanyId = user?.empCompanyId ?? null;
  // 换公司：丢掉上一司 gate，避免误用
  if (
    accessGateCache.empCompanyId != null
    && empCompanyId != null
    && String(accessGateCache.empCompanyId) !== String(empCompanyId)
  ) {
    writeAccessGateCache({ gate: null, empCompanyId, fetchedAt: 0 });
  }
  const now = Date.now();
  if (
    !force
    && accessGateCache.gate
    && accessGateCache.empCompanyId === empCompanyId
    && (now - accessGateCache.fetchedAt) < ACCESS_GATE_TTL_MS
  ) {
    brRuntimeState.kybGateLoading = false;
    return accessGateCache.gate;
  }
  if (accessGateCache.inflight && accessGateCache.empCompanyId === empCompanyId && !force) {
    return accessGateCache.inflight;
  }

  accessGateCache.empCompanyId = empCompanyId;
  // 无可用快照时标 loading，供侧栏「确认中」；有旧 gate 时静默刷新（SWR）
  if (!accessGateCache.gate) {
    brRuntimeState.kybGateLoading = true;
  }
  accessGateCache.inflight = BusinessRegistrationService.GetAccessGate()
    .then((res) => {
      if (!res?.isSuccessful) {
        return accessGateCache.gate;
      }
      const gate = normalizeAccessGate(res.data);
      if (gate) {
        writeAccessGateCache({
          gate,
          empCompanyId,
          fetchedAt: Date.now(),
        });
      }
      return gate || accessGateCache.gate;
    })
    .catch(() => accessGateCache.gate)
    .finally(() => {
      accessGateCache.inflight = null;
      brRuntimeState.kybGateLoading = false;
    });

  return accessGateCache.inflight;
}

export function markBrPostLoginPromptPending() {
  brPostLoginPromptPending = true;
}

export function consumeBrPostLoginPromptPending() {
  if (!brPostLoginPromptPending) {
    return false;
  }
  brPostLoginPromptPending = false;
  return true;
}

export function isBrHomeRoute(path) {
  return path === '/dashboard/default';
}

export function invalidateBrRuntimeCache() {
  brRuntimeState.dataLoadedAt = 0;
  brRuntimeState.nameChangePending = [];
  brRuntimeState.lastLoadError = null;
  invalidateKybAccessGateCache({ clearGate: true });
}

function wrapBrApiCall(promise) {
  return promise
    .then((res) => ({ ok: !!res?.isSuccessful, res }))
    .catch(() => ({ ok: false, res: null }));
}

/** 读取并清空最近一次加载错误（页面 toast 用） */
export function consumeBrRuntimeLoadError() {
  const err = brRuntimeState.lastLoadError;
  brRuntimeState.lastLoadError = null;
  return err;
}

function normalizeNameChangePendingList(data) {
  if (!data) {
    return [];
  }
  const rows = unwrapBrApiRows(data);
  if (rows.length) {
    return rows;
  }
  if (data.approvalId != null || data.ApprovalId != null) {
    return [data];
  }
  return [];
}

function getBrLangId() {
  const langId = Number(localStorage.getItem('currentLangId'));
  return Number.isFinite(langId) && langId > 0 ? langId : 1252;
}

export function syncAuthCompanyNames({ companyNameEn = '', companyNameZh = '' } = {}) {
  const en = String(companyNameEn || '').trim();
  const zh = String(companyNameZh || '').trim();
  if (!en && !zh) {
    return Promise.resolve();
  }

  return import('../store').then(({ store }) => {
    if (!store?.dispatch) {
      return;
    }
    const user = store.state?.authentication?.user;
    if (!user) {
      return;
    }

    const fields = {
      companyNameEn: en || user.companyNameEn || '',
      companyNameTc: zh || user.companyNameTc || '',
      companyNameSc: zh || user.companyNameSc || '',
    };

    // ROOT 同步问候语 displayName；子账号 admin/member 为人名，不可覆盖
    const userType = String(user.userType || '').toUpperCase();
    const userRole = String(user.userRole || user.UserRole || '').toLowerCase();
    const isSubAccount = userRole === 'admin' || userRole === 'member';
    if (userType !== 'ADMIN' && !isSubAccount) {
      const headerName = en || zh;
      if (headerName) {
        fields.displayName = headerName;
      }
    }

    return store.dispatch('authentication/setUserFields', fields);
  }).catch(() => {});
}

function setBrSubmissionStatus(hasSubmitted, summary = null) {
  brRuntimeState.hasSubmitted = !!hasSubmitted;
  brRuntimeState.summary = summary;
}

function setBrDocumentList(items = []) {
  const list = Array.isArray(items) ? items : [];
  brRuntimeState.documentList = list
    .map((item) => normalizeBrApiItem(item))
    .filter((item) => {
      const id = Number(item?.businessRegistrationId);
      return Number.isFinite(id) && id > 0;
    });
}

export function hasBrSubmitted() {
  return brRuntimeState.hasSubmitted === true;
}

export function isBrUnlocked(items = brRuntimeState.documentList) {
  // 本地兜底：任意一张 verified；有 AccessGate 时侧栏以 gate.allowed 为准
  return hasBrVerifiedDocument(items);
}

export async function loadBrSubmissionStatus(force = false) {
  if (!isBrFeatureEnabled()) {
    setBrSubmissionStatus(false, null);
    return false;
  }
  if (statusLoadPromise) {
    return statusLoadPromise;
  }
  const hard = force === 'hard';
  const softForce = force === true;
  if (!hard && !softForce && brRuntimeState.dataLoadedAt > 0) {
    return brRuntimeState.hasSubmitted === true;
  }
  // soft-force 去重：router beforeEach 与 BrPromptHost 常在数秒内连续 force，避免双拉
  const STATUS_FORCE_DEDUPE_MS = 5000;
  if (
    softForce
    && brRuntimeState.dataLoadedAt > 0
    && (Date.now() - brRuntimeState.dataLoadedAt) < STATUS_FORCE_DEDUPE_MS
  ) {
    return brRuntimeState.hasSubmitted === true;
  }
  // force 只刷新 BR 列表/摘要，不清 AccessGate（否则侧栏锁定会丢响应式）
  statusLoadPromise = Promise.all([
    wrapBrApiCall(BusinessRegistrationService.GetSummary({ langId: getBrLangId() })),
    wrapBrApiCall(BusinessRegistrationService.GetList({ langId: getBrLangId() })),
    wrapBrApiCall(BusinessRegistrationService.GetPendingNameChange()),
  ])
    .then(([summaryWrap, listWrap, nameChangeWrap]) => {
      if (!summaryWrap.ok) {
        brRuntimeState.lastLoadError = 'SUMMARY';
        brRuntimeState.dataLoadedAt = 0;
        setBrSubmissionStatus(false, null);
        return false;
      }

      const listFailed = !listWrap.ok;
      const nameChangeFailed = !nameChangeWrap.ok;
      if (listFailed && nameChangeFailed) {
        brRuntimeState.lastLoadError = 'PARTIAL';
      } else if (listFailed) {
        brRuntimeState.lastLoadError = 'LIST';
      } else if (nameChangeFailed) {
        brRuntimeState.lastLoadError = 'NAME_CHANGE';
      } else {
        brRuntimeState.lastLoadError = null;
      }

      const summaryRes = summaryWrap.res;
      const summary = summaryRes?.data || {};
      // 列表失败时保留旧 documentList，避免被当成「无证照」
      if (!listFailed) {
        const listRes = listWrap.res;
        setBrDocumentList(unwrapBrApiRows(listRes?.data));
      }
      if (!nameChangeFailed) {
        brRuntimeState.nameChangePending = normalizeNameChangePendingList(nameChangeWrap.res?.data);
      }

      const items = brRuntimeState.documentList;
      const approvalCode = (summary?.approval?.statusCode || summary?.Approval?.statusCode || '').toUpperCase();
      const submitted = !!(
        summaryRes?.isSuccessful && (
          summary.hasSubmitted
          || approvalCode === BR_STATUS.PENDING
          || approvalCode === BR_STATUS.PENDING_MANUAL
          || approvalCode === BR_STATUS.APPROVED
          || approvalCode === BR_STATUS.REJECTED
          || approvalCode === BR_STATUS.INVALID
          || hasBrListSubmissionActivity(items)
        )
      );
      setBrSubmissionStatus(submitted, summary);
      if (summaryRes?.isSuccessful
        && ('currentNameEn' in summary || 'CurrentNameEn' in summary
          || 'currentNameZh' in summary || 'CurrentNameZh' in summary)) {
        const companyNameEn = String(summary.currentNameEn ?? summary.CurrentNameEn ?? '').trim();
        const companyNameZh = String(summary.currentNameZh ?? summary.CurrentNameZh ?? '').trim();
        syncAuthCompanyNames({ companyNameEn, companyNameZh });
      }
      brRuntimeState.dataLoadedAt = Date.now();
      // 列表已更新但 AccessGate 为空时立刻回填，缩短「未知则从严」窗口
      if (!accessGateCache.gate) {
        import('../store').then(({ store }) => {
          const user = store?.state?.authentication?.user;
          if (user?.empCompanyId) {
            return fetchKybAccessGate(user, { force: true });
          }
          return null;
        }).catch(() => {});
      }
      return submitted;
    })
    .catch(() => {
      brRuntimeState.lastLoadError = 'SUMMARY';
      brRuntimeState.dataLoadedAt = 0;
      setBrSubmissionStatus(false, null);
      return false;
    })
    .finally(() => {
      statusLoadPromise = null;
    });
  return statusLoadPromise;
}
